import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLightbulb,
  faBolt,
  faCog,
  faChartBar,
  faArrowUp,
  faComments,
  faNetworkWired,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./CaseStudyPrism.module.css";
import { useEffect } from "react";
import { useAppDispatch } from "../../common/state/hooks";
import { update } from "../../common/state/Slices/HeaderMenuSlice";
import { Routes } from "../../routes/routes";
import PrismSecond from "../../images/case-studies/prism/Prism-2.png";

const caseStudy = {
  title: "PRISM Project and the Miné Platform Built on Garuda",
  overview:
    "The PRISM Project represents a collaborative effort to enhance drug discovery using advanced AI tools and data integration. Central to this initiative is the Miné Platform, designed to enable seamless workflows and data-driven insights in biomedical research. At the heart of Miné’s capabilities lies Garuda, a powerful framework for connecting, automating, and scaling diverse tools and datasets.Garuda’s Role in Powering the Miné Platform",
  subOverview:
    "The Miné Platform was built on the foundation of Garuda, leveraging its key strengths to solve complex challenges in data analysis and AI integration. With Garuda, the platform ensures connectivity, automation, and the flexibility needed for cutting-edge drug discovery workflows.",
  features: [
    {
      title: "Unified Access to AI Tools",
      description:
        "Garuda organizes AI applications into a single, easy-to-use interface, giving researchers centralized access to tools for target identification, pattern recognition, and more.",
    },
    {
      title: "Workflow Execution with Pre-Built Recipes",
      description:
        "Using Garuda’s Recipe Player, researchers can execute pre-designed workflows that incorporate multiple tools and datasets. These workflows ensure reliable and repeatable analysis processes.",
    },
    {
      title: "Dynamic Data Integration",
      description:
        "Garuda connects data from diverse sources, ensuring that the Miné Platform provides a comprehensive view of complex datasets for diseases like lung cancer and idiopathic pulmonary fibrosis (IPF).",
    },
    {
      title: "User Management",
      description: "Tailored access controls for each user group for both analysis and data. Trace of data access and analysis."
    }
  ],
  benefits: [
    {
      title: "Connectivity Across Tools and Data",
      description:
        "Garuda enables the integration of multiple AI tools into the Miné Platform, allowing researchers to work across diverse datasets, including genomic, epigenomic, and transcriptomic data. Its connectivity ensures that each tool functions as part of a unified ecosystem.",
    },
    {
      title: "Interoperable Workflows",
      description:
        "By using Garuda’s modular framework, the Miné Platform allows AI tools to communicate seamlessly, making it easier to execute workflows that analyze and interpret large datasets. Garuda’s Agent-based design ensures that tools are interoperable and adaptable to evolving research needs.",
    },
    {
      title: "Scalable Automation",
      description:
        "Built with scalability in mind, Garuda allows the Miné Platform to automate repetitive tasks using pre-configured workflows. Researchers can use these workflows to reduce manual intervention, ensuring consistency and faster insights.",
    },
  ],
  role: {
    title:
      "The Miné Platform, powered by Garuda, enhances critical stages of the drug discovery process:",
    points: [
      {
        title: "Target Identification and patient stratification",
        description:
          "AI tools analyze integrated datasets to pinpoint potential therapeutic targets.",
      },
      {
        title: "Data Analysis",
        description:
          "Garuda’s connectivity allows for seamless data integration and advanced analysis across different omics layers.",
      },
      {
        title: "Scalable Workflows",
        description:
          "Pre-built and automated workflows reduce operational complexity and improve efficiency.",
      },
    ],
  },
  impacts: [
    {
      primaryText: "The Impact of Garuda in the PRISM Project",
      secondaryText:
        "Garuda transforms the Miné Platform into a highly interoperable and scalable solution. By providing the infrastructure to connect diverse tools and automate workflows, Garuda ensures that researchers can focus on generating actionable insights rather than managing technical complexities. This collaboration highlights how Garuda enables cutting-edge innovations in biomedical research.",
      icon: faNetworkWired
    },
    {
      primaryText: "User benefits",
      icon: faUser,
      secondaryText:
        "Traceability of analysis via comprehensive logging feature",
    }
  ],
  learn: {
    primaryText:
      "Visit Miné Tool Commons",
    links: [
     {
      title: "JP",
      url: "https://www.nibiohn.go.jp/mine/jp/",
     },
     {
      title: "EN",
      url: "https://www.nibiohn.go.jp/mine/index.html",
     }
    ],
  },
};
const icons = [faLightbulb, faBolt, faCog, faChartBar, faArrowUp, faComments];

export const CaseStudyPrism = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(
      update({
        selectedMenu: Routes.CASE_STUDIES_PRISM,
      })
    );
  }, [dispatch]);

  return (
    <Box>
      <Card
        key={caseStudy.title}
        sx={{
          margin: 2,
          borderRadius: 2,
          boxShadow:
            "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
        }}
      >
        <CardContent>
          <Typography
            variant="h2"
            color="primary"
            gutterBottom
            sx={{ fontWeight: "bold", mt: 2 }}
          >
            <span style={{ color: "#454545" }}>Case Study: </span>
            {caseStudy.title}
          </Typography>
          <Typography variant="body2" paragraph sx={{ mt: 4 }}>
            {caseStudy.overview}
          </Typography>

          <Typography variant="body2" paragraph>
            {caseStudy.subOverview}
          </Typography>
          <div className={styles.caseStudyImageContainer}>
            <div className={styles.caseStudyImageWrapper}>
              <img
                src={PrismSecond}
                alt="Prism Second"
                className={styles.image}
              />
            </div>
          </div>
          <Divider sx={{ marginY: 2 }} />
          <Typography variant="h3" gutterBottom sx={{ fontWeight: "500" }}>
            Applications and Benefits:
          </Typography>
          <List>
            {caseStudy.benefits.map((benefit, idx) => (
              <ListItem key={idx} sx={{ alignItems: "baseline" }}>
                <ListItemIcon sx={{ minWidth: "auto" }}>
                  <FontAwesomeIcon icon={icons[idx % icons.length]} size="lg" />
                </ListItemIcon>
                <ListItemText
                  sx={{ textAlign: "start", margin: "0 1rem" }}
                  primary={
                    <>
                      <strong>{benefit.title}:</strong> {benefit.description}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ marginY: 2 }} />
          <Typography variant="h3" gutterBottom sx={{ fontWeight: "500" }}>
            Features of the Miné Platform Enabled by Garuda:
          </Typography>
          <List>
            {caseStudy.features.map((feature, idx) => (
              <ListItem key={idx} sx={{ alignItems: "baseline" }}>
                <ListItemIcon sx={{ minWidth: "auto" }}>
                  <FontAwesomeIcon icon={icons[idx % icons.length]} size="lg" />
                </ListItemIcon>
                <ListItemText
                  sx={{ textAlign: "start", margin: "0 1rem" }}
                  primary={
                    <>
                      <strong>{feature.title}:</strong> {feature.description}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ marginY: 2 }} />
          <Typography variant="h3" gutterBottom sx={{ fontWeight: "500" }}>
            How Miné Advances Drug Discovery with Garuda:
          </Typography>
          <Typography variant="body1" gutterBottom>
            {caseStudy.role.title}
          </Typography>
          <List>
            {caseStudy.role.points.map((point, idx) => (
              <ListItem key={point.title} sx={{ alignItems: "baseline" }}>
                <ListItemIcon sx={{ minWidth: "auto" }}>
                  <FontAwesomeIcon icon={icons[idx % icons.length]} size="lg" />
                </ListItemIcon>
                <ListItemText
                  sx={{ textAlign: "start", margin: "0 1rem" }}
                  primary={
                    <>
                      <strong>{point.title}:</strong> {point.description}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ marginY: 2 }} />
          <Typography variant="h3" gutterBottom sx={{ fontWeight: "500" }}>
            Impact:
          </Typography>
          <List>
            {caseStudy.impacts.map((impact, idx) => (
              <ListItem
                  key={impact.primaryText}
                  sx={{ alignItems: "baseline" }}
                >
                  <ListItemIcon sx={{ minWidth: "auto" }}>
                    <FontAwesomeIcon icon={impact.icon} size="lg" />
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      textAlign: "start",
                      margin: "0 1rem",
                      "& .MuiTypography-root": {
                        fontWeight: 400,
                      },
                      "& .MuiTypography-body2": {
                        color: "text.primary",
                      },
                    }}
                    primary={impact.primaryText}
                    secondary={impact.secondaryText}
                  />
                </ListItem>
            ))}
          </List>
          <Divider sx={{ marginY: 2 }} />
          <Typography variant="h3" gutterBottom sx={{ fontWeight: "500" }}>
            Learn More:
          </Typography>
          <List>
          <ListItem
                        key={caseStudy.learn.primaryText}
                        sx={{ alignItems: "baseline" }}
                      >
                        <ListItemIcon sx={{ minWidth: "auto" }}>
                          <FontAwesomeIcon icon={faNetworkWired} size="lg" />
                        </ListItemIcon>
                        <ListItemText
                          sx={{
                            textAlign: "start",
                            margin: "0 1rem",
                            "& .MuiTypography-root": {
                              fontWeight: 400,
                            },
                            "& .MuiTypography-body2": {
                              color: "text.primary",
                            },
                          }}
                          primary={caseStudy.learn.primaryText}
                        />
                      </ListItem>
          {caseStudy.learn.links.map((link, idx) => (
              <ListItem key={link.title} sx={{ alignItems: "baseline", padding: 0 }}>
                <ListItemText
                  sx={{
                    textAlign: "start",
                    margin: "8",
                  }}
                  primary={
                    <Typography variant="subtitle1" sx={{display: "flex", flexDirection: "row"}}>
                        {link.title}:&nbsp;
                        <a
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: "1rem", display: "block" }}
                      >
                        {link.url}
                      </a>
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};
